import { useCallback, useEffect } from "react";

import type { ConfigDTO } from "../../common/types/ConfigDTO";
import { useAppSelector } from "../store";
import { useLazyCheckAuthenticationQuery } from "../store/services/authentication";
import { selectRedirectUri } from "../store/services/config";
import { navigationChannel, USER_UNAUTHENTICATED } from "../utils/broadcastChannel";
import { redirectToAuthorise } from "../utils/oauth";
import logger from "../utils/logger";

// Listen for iFrame app to tell navigation that token is no longer valid
// Check token again when message received and redirect to login if invalid
export const useAuthListener = (config?: ConfigDTO) => {
  const verifyTokenUri = config?.verifyTokenUri;
  const redirectUri = useAppSelector(selectRedirectUri);
  const [checkAuth, checkAuthResult] = useLazyCheckAuthenticationQuery();

  // TODO: Remove this once all apps are using broadcast channel
  const sendMessage = useCallback(
    (event: MessageEvent) => {
      if (event.origin.includes("dexibit.io") || event.origin.includes("dexibit.com")) {
        const message = event.data;
        if (message === USER_UNAUTHENTICATED && verifyTokenUri) {
          logger.debug("Received USER_UNAUTHENTICATED via postMessage");
          void checkAuth({ verifyTokenUri });
        }
      }
    },
    [checkAuth, verifyTokenUri],
  );

  useEffect(() => {
    if (verifyTokenUri) {
      navigationChannel.addEventListener("message", (event: MessageEvent) => {
        if (event.data === USER_UNAUTHENTICATED) {
          logger.debug("Received USER_UNAUTHENTICATED via BroadcastChannel");
          void checkAuth({ verifyTokenUri });
        }
      });
      window.addEventListener("message", sendMessage);
    }

    return () => {
      window.removeEventListener("message", sendMessage);
    };
  }, [verifyTokenUri, checkAuth, sendMessage]);

  useEffect(() => {
    const { isUninitialized, isFetching, isError } = checkAuthResult;
    if (!isUninitialized && !isFetching && isError && redirectUri) {
      redirectToAuthorise(redirectUri);
    }
  }, [checkAuthResult, redirectUri]);
};
